import React, { useEffect, useState } from 'react';
import { FaQuestionCircle, FaSearch, FaEnvelope, FaPhone, FaChevronDown, FaChevronUp, FaLightbulb } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { getCompanyDetails } from '../config/CompanyDetails';
import { TUTORIALS, FAQS } from './clientHelpContent';

function ClientHelp({ onLogout }) {
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [searchTerm, setSearchTerm] = useState('');
  const [openTutorial, setOpenTutorial] = useState('dashboard');
  const [company, setCompany] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getCompanyDetails()
      .then((c) => { if (!cancelled) setCompany(c); })
      .catch(() => { /* fall back to the contact details at the bottom of the page */ });
    return () => { cancelled = true; };
  }, []);

  const TERM = searchTerm.trim().toLowerCase();
  const visibleTutorials = !TERM
    ? TUTORIALS
    : TUTORIALS.filter((t) => [t.title, t.summary, t.tip || '', ...t.steps].join(' ').toLowerCase().includes(TERM));
  const filteredFaqs = FAQS.filter((f) => !TERM
    || f.q.toLowerCase().includes(TERM)
    || f.a.toLowerCase().includes(TERM));

  return (
    <ClientLayout activeTab="help" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>❓ Help &amp; Tutorials</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            Step-by-step guides for every page in your portal, plus answers to the usual questions.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            type="text"
            placeholder="Search tutorials and FAQs — POD, quote, invoice, cell number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:border-green"
            style={{ borderColor: '#E8ECEF' }}
          />
        </div>
        {TERM ? (
          <p className="text-xs mt-3" style={{ color: '#4A5568' }}>
            {visibleTutorials.length} tutorial{visibleTutorials.length === 1 ? '' : 's'} and {filteredFaqs.length} answer{filteredFaqs.length === 1 ? '' : 's'} match your search.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-4">
            {TUTORIALS.map((t) => (
              <button
                key={t.id}
                onClick={() => setOpenTutorial(t.id)}
                className="px-3 py-1.5 rounded-full border text-xs font-medium hover:bg-gray-50"
                style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
              >
                {t.icon} {t.title}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#0A1929' }}>Tutorials</h2>
        {visibleTutorials.length === 0 ? (
          <p className="text-sm" style={{ color: '#4A5568' }}>No tutorials match your search. Try another word, or check the FAQs below.</p>
        ) : (
          <div className="space-y-3">
            {visibleTutorials.map((t) => {
              const isOpen = openTutorial === t.id;
              return (
                <div key={t.id} className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E8ECEF' }}>
                  <button
                    onClick={() => setOpenTutorial(isOpen ? null : t.id)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50"
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-xl">{t.icon}</span>
                      <span>
                        <span className="block font-semibold" style={{ color: '#0A1929' }}>{t.title}</span>
                        <span className="block text-xs mt-0.5" style={{ color: '#4A5568' }}>{t.summary}</span>
                      </span>
                    </span>
                    {isOpen ? <FaChevronUp size={12} style={{ color: '#94A3B8' }} /> : <FaChevronDown size={12} style={{ color: '#94A3B8' }} />}
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <ol className="space-y-2 text-sm list-decimal list-inside" style={{ color: '#4A5568' }}>
                        {t.steps.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                      {t.tip ? (
                        <p className="mt-3 flex items-start gap-2 text-xs rounded-lg p-3" style={{ backgroundColor: '#f0fdf4', color: '#047857' }}>
                          <FaLightbulb size={12} className="mt-0.5 flex-shrink-0" />
                          <span>{t.tip}</span>
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-bold mb-3" style={{ color: '#0A1929' }}>Frequently asked questions</h2>
        {filteredFaqs.length === 0 ? (
          <p className="text-sm" style={{ color: '#4A5568' }}>No answers match your search — contact us using the details below.</p>
        ) : (
          <div className="space-y-3">
            {filteredFaqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border p-5" style={{ borderColor: '#E8ECEF' }}>
                <h3 className="font-semibold mb-1 flex items-center gap-2 text-sm" style={{ color: '#0A1929' }}>
                  <FaQuestionCircle style={{ color: '#00A86B' }} /> {faq.q}
                </h3>
                <p className="text-sm" style={{ color: '#4A5568' }}>{faq.a}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 bg-white rounded-xl border p-6 text-center" style={{ borderColor: '#E8ECEF' }}>
        <p className="text-sm font-medium mb-1" style={{ color: '#0A1929' }}>Still need help?</p>
        <p className="text-xs mb-4" style={{ color: '#4A5568' }}>
          Your account manager is one call away — or send us an email and we will come back to you the same working day.
        </p>
        <div className="flex flex-wrap justify-center gap-6">
          <a
            href={`mailto:${(company && company.company_email) || 'info@onepurposelogistics.com'}`}
            className="flex items-center gap-2 text-sm hover:underline"
            style={{ color: '#4A5568' }}
          >
            <FaEnvelope style={{ color: '#00A86B' }} /> {(company && company.company_email) || 'info@onepurposelogistics.com'}
          </a>
          {company && company.company_phone ? (
            <a href={`tel:${company.company_phone}`} className="flex items-center gap-2 text-sm hover:underline" style={{ color: '#4A5568' }}>
              <FaPhone style={{ color: '#00A86B' }} /> {company.company_phone}
            </a>
          ) : null}
        </div>
      </div>
    </ClientLayout>
  );
}

export default ClientHelp;
